// iter-121.118 — EA Recent Activity feed (kept per user direction 2b)
//
// Activity log filtered by EA's tenant with kind pills (All / Uploads /
// Dashboards / Sense AI / Team). Kind→action-prefix mapping lives
// server-side in /home/ea/recent-activity so the UI never hardcodes the
// action vocabulary.
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useEaScope } from "@/lib/ea-scope";
import {
    Activity, Upload as UploadIcon, LayoutDashboard, Sparkles, Users,
} from "lucide-react";

const KIND_PILLS = [
    { key: null,         label: "All",        Icon: Activity },
    { key: "upload",     label: "Uploads",    Icon: UploadIcon },
    { key: "dashboard",  label: "Dashboards", Icon: LayoutDashboard },
    { key: "ai",         label: "Sense AI",   Icon: Sparkles },
    { key: "team",       label: "Team",       Icon: Users },
];

function timeFromNow(iso) {
    if (!iso) return "—";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60)     return "just now";
    if (diff < 3600)   return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400)  return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
}

function ActivityRow({ row }) {
    const action = (row.action || "").replace(/_/g, " ");
    const who = row.user_name || (row.user_email || "").split("@")[0] || "Someone";
    return (
        <div className="flex items-start gap-3 py-2 border-t border-border first:border-t-0"
             data-testid="ea-activity-row">
            <div className="text-[11px] text-muted-foreground w-16 shrink-0">
                {timeFromNow(row.created_at)}
            </div>
            <div className="flex-1 min-w-0 text-[12px]">
                <span className="font-medium">{who}</span>{" "}
                <span className="text-muted-foreground">{action}</span>
                {row.target_name && (
                    <>
                        {" "}<span className="font-medium">{row.target_name}</span>
                    </>
                )}
            </div>
        </div>
    );
}

export default function EaRecentActivity({ onEmpty }) {
    const { queryString } = useEaScope();
    const [kind, setKind] = useState(null);
    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        const params = new URLSearchParams(queryString || "");
        params.set("limit", "20");
        if (kind) params.set("kind", kind);
        try {
            const r = await api.get(`/home/ea/recent-activity?${params}`);
            setRows(r.data?.rows || []);
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [queryString, kind]);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    useEffect(() => {
        if (onEmpty && rows !== null) onEmpty(rows.length === 0 && !kind);
    }, [rows, kind, onEmpty]);

    return (
        <div data-testid="ea-recent-activity">
            <div className="flex items-center justify-between mb-2">
                <div className="eyebrow text-[10px]">Recent Activity</div>
            </div>
            <div className="flex flex-wrap gap-1 mb-3">
                {KIND_PILLS.map((p) => {
                    const active = p.key === kind;
                    return (
                        <button
                            key={p.key || "all"}
                            type="button"
                            onClick={() => setKind(p.key)}
                            className={`inline-flex items-center gap-1 px-2 h-7 rounded-sm border text-[11px] transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-border bg-card hover:bg-secondary/40"}`}
                            data-testid={`ea-activity-pill-${p.key || "all"}`}
                            data-state={active ? "active" : "inactive"}
                        >
                            <p.Icon className="h-3 w-3" />
                            {p.label}
                        </button>
                    );
                })}
            </div>
            <div className="border border-border rounded-sm bg-card p-3 max-h-[420px] overflow-y-auto">
                {loading ? (
                    <div className="text-xs text-muted-foreground py-4 text-center">Loading activity…</div>
                ) : !rows || rows.length === 0 ? (
                    <div className="text-xs text-muted-foreground py-6 text-center"
                         data-testid="ea-activity-empty">
                        No activity in the selected scope.
                    </div>
                ) : (
                    rows.map((r, i) => <ActivityRow key={r.id || i} row={r} />)
                )}
            </div>
        </div>
    );
}
